import React, { useEffect, useState } from 'react';
import { ReactComponent as StepDeleteIcon } from '../../../../assets/svg/stepDeleteIcon.svg';
import { Autocomplete, IconButton, TextField } from '@mui/material';
import {
  columnsAutocompleteLiStyles,
  columnsAutocompletePlaceholderStyles,
  columnsAutocompleteStyles,
  removeIconStyles,
  wholeStyles
} from './processingGroupByInnerBlock.styles';
import { IProcessingGroupByInnerBlock } from './processingGroupByInnerBlock.models';
import { groupByActionsOptions } from './processingGroupByInnerBlock.constants';
import { IProcessingGroupByBlock } from '../processingGroupBy.models';

const ProcessingGroupByInnerBlock: React.FC<IProcessingGroupByInnerBlock> = ({
  resetCounter,
  dropdownOptions,
  updateSingleActionBody,
  setBlocks,
  block,
  blocks,
  element,
  deleteBlock,
  onOptionsExhausted,
  data
}) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    block.singleActionBody.aggregation_columns?.length > 0 ? block.singleActionBody.aggregation_columns : []
  );
  const [selectedActions, setSelectedActions] = useState<string[]>(
    block.singleActionBody.aggregation_function?.length > 0 ? block.singleActionBody.aggregation_function : []
  );

  const getFilteredDropdownOptions = (): { id: number; label: string }[] => {
    const selectedColumnsInOtherBlocks: string[] = blocks
      .filter((b: IProcessingGroupByBlock) => b.id !== block.id)
      .flatMap((b: IProcessingGroupByBlock) => b.singleActionBody?.aggregation_columns || []);

    return dropdownOptions.filter(
      (option: { id: number; label: string }) =>
        !selectedColumnsInOtherBlocks.includes(option.label) && !selectedColumns.includes(option.label)
    );
  };

  const handleColumnsChange = (_: any, newValue: Array<{ id: number; label: string }>) => {
    setSelectedColumns(newValue.map((option) => option.label));
  };

  const handleActionsChange = (_: any, newValue: Array<{ id: number; label: string }>) => {
    setSelectedActions(newValue.map((option) => option.label));
  };

  const allFieldsAreFilled = selectedColumns.length > 0 && selectedActions.length > 0;

  const isPlusDisabled = dropdownOptions.length === 1;

  useEffect(() => {
    setSelectedColumns([]);
    setSelectedActions([]);
  }, [resetCounter]);

  useEffect(() => {
    onOptionsExhausted(isPlusDisabled); // Notify the parent if options are exhausted
  }, [dropdownOptions]);

  useEffect(() => {
    const newActionBody = {
      aggregation_columns: selectedColumns,
      aggregation_function: selectedActions,
      isCompleted: allFieldsAreFilled // Mark the block as completed if both columns are selected
    };
    const updatedBlocks = updateSingleActionBody(blocks, block.id, newActionBody);
    setBlocks(updatedBlocks);
  }, [selectedColumns, selectedActions]);

  return (
    <div style={wholeStyles}>
      <div>
        <Autocomplete
          multiple
          limitTags={1}
          // value={dropdownOptions.filter((option: { id: number; label: string }) =>
          //   selectedColumns.includes(option.label)
          // )}
          value={selectedColumns
            .map((label) => dropdownOptions.find((option: any) => option.label === label)!)
            .filter(Boolean)}
          options={getFilteredDropdownOptions()}
          onChange={handleColumnsChange}
          disableCloseOnSelect={true}
          sx={columnsAutocompleteStyles(blocks)}
          disableClearable={false}
          renderOption={(props, option) => (
            <li {...props} style={columnsAutocompleteLiStyles}>
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select columns for aggregation"
              label=""
              InputProps={{
                ...params.InputProps,
                style: columnsAutocompleteLiStyles
              }}
              sx={columnsAutocompletePlaceholderStyles}
            />
          )}
          componentsProps={{
            clearIndicator: {
              onClick: () => setSelectedColumns([]) // Clear selections
            }
          }}
        />
      </div>

      <div>
        <Autocomplete
          multiple
          limitTags={1}
          // value={groupByActionsOptions.filter((option: { id: number; label: string }) =>
          //   selectedActions.includes(option.label)
          // )}
          value={selectedActions
            .map((label) => groupByActionsOptions.find((option) => option.label === label)!)
            .filter(Boolean)}
          options={groupByActionsOptions.filter((option) => !selectedActions.includes(option.label))}
          onChange={handleActionsChange}
          disableCloseOnSelect={true}
          sx={columnsAutocompleteStyles(blocks)}
          renderOption={(props, option) => (
            <li {...props} style={columnsAutocompleteLiStyles}>
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="select aggregation function"
              label=""
              InputProps={{
                ...params.InputProps,
                style: columnsAutocompleteLiStyles
              }}
              sx={columnsAutocompletePlaceholderStyles}
            />
          )}
          componentsProps={{
            clearIndicator: {
              onClick: () => setSelectedActions([]) // Clear selections
            }
          }}
        />
      </div>

      {/* Delete button (appears if there's more than one block) */}
      {blocks.length > 1 && (
        <IconButton style={removeIconStyles(element)} onClick={() => deleteBlock(element)} disableRipple={true}>
          <StepDeleteIcon />
        </IconButton>
      )}
    </div>
  );
};

export default ProcessingGroupByInnerBlock;
